import * as XLSX from 'xlsx';
import {controlColumnLabel, displayDate, excelDate, formatRate, paymentTypeLabel} from './AccountingFormat';
import {journalsSuffix} from './JournalSelection';
import {
    DATE_FORMAT, INTEGER_FORMAT, MONEY_FORMAT, addFreeSheet, addTable, cell, downloadWorkbook, round2, sumCell, text, total
} from './ExcelTable';

function addSummary(workbook, exportData) {
    const warnings = exportData.controls.filter(control => control.level === 'warning').length;
    const infos = exportData.controls.length - warnings;
    const totals = exportData.totals;
    const purchaseJournal = exportData.journals.find(journal => journal.code === 'AC');
    // Export limité à certains journaux : seules les écritures, les journaux et la balance des tiers suivent la sélection
    const restricted = !!exportData.selectedJournals;
    addFreeSheet(workbook, 'Synthèse', [
        [text('Export comptable (FEC)')],
        [],
        [text('Entreprise'), cell(exportData.company?.name)],
        [text('SIREN'), cell(exportData.company?.siren)],
        [text('Période'), text(`du ${displayDate(exportData.period.start)} au ${displayDate(exportData.period.end)}`)],
        [text('Exporté le'), text(new Date().toLocaleDateString('fr-FR'))],
        [text('Journaux exportés'), text(restricted ? exportData.selectedJournals.join(', ') : 'Tous les journaux')],
        [],
        [text('Écritures'), cell(totals.count, INTEGER_FORMAT)],
        [text('Lignes d\'écriture'), cell(totals.lines, INTEGER_FORMAT)],
        ...(exportData.hasPurchases && (!restricted || purchaseJournal) ? [[text('Écritures d\'achat (journal AC)'), cell(purchaseJournal ? purchaseJournal.count : 0, INTEGER_FORMAT)]] : []),
        [text('Total débit'), cell(totals.debit, MONEY_FORMAT)],
        [text('Total crédit'), cell(totals.credit, MONEY_FORMAT)],
        [],
        [text('Contrôles : points d\'attention'), cell(warnings, INTEGER_FORMAT)],
        [text('Contrôles : informations'), cell(infos, INTEGER_FORMAT)],
        ...(restricted ? [[], [text('La TVA et les contrôles portent sur l\'ensemble de la période, quels que soient les journaux exportés.')]] : []),
    ], 2);
}

function addEntries(workbook, entries) {
    addTable(workbook, 'Écritures', [
        {header: 'N° écriture', pick: entry => entry.ecritureNum, format: INTEGER_FORMAT},
        {header: 'Date', pick: entry => excelDate(entry.ecritureDate), format: DATE_FORMAT},
        {header: 'Journal', pick: entry => entry.journalCode},
        {header: 'Compte', pick: entry => entry.compteNum},
        {header: 'Libellé compte', pick: entry => entry.compteLib},
        {header: 'Compte auxiliaire', pick: entry => entry.compAuxNum},
        {header: 'Libellé auxiliaire', pick: entry => entry.compAuxLib},
        {header: 'Pièce', pick: entry => entry.pieceRef},
        {header: 'Libellé écriture', pick: entry => entry.ecritureLib},
        {header: 'Débit', pick: entry => entry.debit || undefined, format: MONEY_FORMAT, total: true},
        {header: 'Crédit', pick: entry => entry.credit || undefined, format: MONEY_FORMAT, total: true},
        {header: 'Lettrage', pick: entry => entry.ecritureLet},
        {header: 'Date de lettrage', pick: entry => excelDate(entry.dateLet), format: DATE_FORMAT},
    ], entries);
}

function addJournals(workbook, journals) {
    addTable(workbook, 'Journaux', [
        {header: 'Journal', pick: journal => journal.code},
        {header: 'Libellé', pick: journal => journal.label},
        {header: 'Écritures', pick: journal => journal.count, format: INTEGER_FORMAT, total: true},
        {header: 'Débit', pick: journal => journal.debit, format: MONEY_FORMAT, total: true},
        {header: 'Crédit', pick: journal => journal.credit, format: MONEY_FORMAT, total: true},
    ], journals);
}

// Balance des comptes de tiers (411 clients, 419 acomptes reçus, 401 fournisseurs), un solde par compte et par client ou fournisseur
function addBalances(workbook, balances) {
    addTable(workbook, 'Balance tiers', [
        {header: 'Compte', pick: balance => balance.compteNum},
        {header: 'Libellé compte', pick: balance => balance.compteLib},
        {header: 'Compte auxiliaire', pick: balance => balance.compAuxNum},
        {header: 'Client / fournisseur', pick: balance => balance.compAuxLib},
        {header: 'Débit', pick: balance => balance.debit, format: MONEY_FORMAT, total: true},
        {header: 'Crédit', pick: balance => balance.credit, format: MONEY_FORMAT, total: true},
        {header: 'Solde débiteur', pick: balance => balance.balance > 0 ? balance.balance : undefined, format: MONEY_FORMAT, total: true},
        {header: 'Solde créditeur', pick: balance => balance.balance < 0 ? -balance.balance : undefined, format: MONEY_FORMAT, total: true},
    ], balances);
}

const VAT_AMOUNTS = ['ht', 'tva', 'ttc'];

/** Cellules « écart » (base HT, TVA, TTC) entre deux blocs de TVA : formules sur leurs lignes de total */
function differenceCells(minuendRow, subtrahendRow, minuend, subtrahend) {
    return VAT_AMOUNTS.map((key, index) => {
        const column = XLSX.utils.encode_col(index + 1);
        return {t: 'n', f: `${column}${minuendRow}-${column}${subtrahendRow}`, z: MONEY_FORMAT,
            v: round2(total(minuend, row => row[key]) - total(subtrahend, row => row[key]))};
    });
}

/**
 * TVA facturée (sur les débits), TVA encaissée, et ce qui reste à encaisser ; avec les achats : TVA déductible, TVA décaissée,
 * ce qui reste à décaisser et la TVA à payer (collectée moins déductible).
 */
function addVat(workbook, vat, hasPurchases) {
    const aoa = [];
    const block = (title, rows) => {
        aoa.push([text(title)]);
        aoa.push(['Taux', 'Base HT', 'TVA', 'TTC'].map(text));
        const first = aoa.length + 1;
        rows.forEach(row => aoa.push([text(formatRate(row.rate)), ...VAT_AMOUNTS.map(key => cell(row[key], MONEY_FORMAT))]));
        const totalRow = aoa.length + 1;
        aoa.push([text('Total'), ...VAT_AMOUNTS.map((key, index) => rows.length > 0
            ? sumCell(index + 1, first, totalRow - 1, total(rows, row => row[key]))
            : cell(0, MONEY_FORMAT))]);
        aoa.push([]);
        return totalRow;
    };
    const salesTotal = block('TVA facturée (sur les débits) : ventes nettes des avoirs et des acomptes déduits', vat.sales);
    const collectedTotal = block('TVA encaissée (sur les encaissements) : règlements nets des remboursements', vat.collected);

    aoa.push([text('Facturé et pas encore encaissé (TVA à encaisser)')]);
    aoa.push([text('Écart'), ...differenceCells(salesTotal, collectedTotal, vat.sales, vat.collected)]);

    if (hasPurchases) {
        aoa.push([]);
        const purchasesTotal = block('TVA déductible (sur les débits) : factures d\'achat', vat.purchases);
        const paidTotal = block('TVA décaissée (sur les décaissements) : paiements des achats', vat.paid);
        aoa.push([text('Facturé par les fournisseurs et pas encore payé (TVA à décaisser)')]);
        aoa.push([text('Écart'), ...differenceCells(purchasesTotal, paidTotal, vat.purchases, vat.paid)]);
        aoa.push([]);
        aoa.push([text('TVA à payer (collectée - déductible)')]);
        aoa.push([text('Sur les débits (ventes facturées - achats facturés)'), null, differenceCells(salesTotal, purchasesTotal, vat.sales, vat.purchases)[1]]);
        aoa.push([text('Sur les encaissements et décaissements'), null, differenceCells(collectedTotal, paidTotal, vat.collected, vat.paid)[1]]);
    }

    addFreeSheet(workbook, 'TVA', aoa, 4);
}

/** Détail de la TVA des règlements, une ligne par règlement et par taux : encaissements (clients) ou décaissements (fournisseurs) */
function addVatPayments(workbook, sheetName, partyHeader, partyKey, ttcHeader, details) {
    addTable(workbook, sheetName, [
        {header: 'Date', pick: row => excelDate(row.date), format: DATE_FORMAT},
        {header: 'Pièce', pick: row => row.pieceRef},
        {header: partyHeader, pick: row => row[partyKey]},
        {header: 'Taux', pick: row => formatRate(row.rate)},
        {header: ttcHeader, pick: row => row.ttc, format: MONEY_FORMAT, total: true},
        {header: 'Base HT', pick: row => row.ht, format: MONEY_FORMAT, total: true},
        {header: 'TVA', pick: row => row.tva, format: MONEY_FORMAT, total: true},
    ], details);
}

/** Contrôles : une ligne par objet à traiter (ou une ligne par contrôle sans détail) */
function addControls(workbook, controls) {
    const rows = controls.flatMap(control => {
        const base = {level: control.level === 'warning' ? 'Attention' : 'Information', message: control.message, action: control.action || ''};
        if (!control.rows || control.rows.length === 0) return [base];
        return control.rows.map(row => {
            const pick = column => (control.columns.includes(column) ? row[column] : undefined);
            return {
                ...base, date: pick('date'), amount: pick('amount'), paymentType: pick('paymentType'),
                contractNumber: pick('contractNumber'), clientName: pick('clientName'), supplierName: pick('supplierName'), pieceRef: pick('pieceRef'), info: pick('info'),
            };
        });
    });
    addTable(workbook, 'Contrôles', [
        {header: 'Niveau', pick: row => row.level},
        {header: 'Contrôle', pick: row => row.message},
        {header: 'Action à réaliser', pick: row => row.action},
        {header: controlColumnLabel('date'), pick: row => excelDate(row.date), format: DATE_FORMAT},
        {header: controlColumnLabel('amount'), pick: row => row.amount, format: MONEY_FORMAT},
        {header: controlColumnLabel('paymentType'), pick: row => row.paymentType ? paymentTypeLabel(row.paymentType) : undefined},
        {header: controlColumnLabel('contractNumber'), pick: row => row.contractNumber, format: INTEGER_FORMAT},
        {header: controlColumnLabel('clientName'), pick: row => row.clientName},
        {header: controlColumnLabel('supplierName'), pick: row => row.supplierName},
        {header: controlColumnLabel('pieceRef'), pick: row => row.pieceRef},
        {header: controlColumnLabel('info'), pick: row => row.info},
    ], rows);
}

/**
 * Classeur Excel : synthèse, écritures (le FEC lisible), journaux, balance des comptes de tiers (clients et fournisseurs), TVA et contrôles.
 * Les dates et les montants sont de vrais nombres formatés (triables, filtrables), les totaux sont des formules.
 */
export function downloadExcel(exportData) {
    const workbook = XLSX.utils.book_new();
    addSummary(workbook, exportData);
    addEntries(workbook, exportData.entries);
    addJournals(workbook, exportData.journals);
    addBalances(workbook, exportData.balances);
    addVat(workbook, exportData.vat, exportData.hasPurchases);
    addVatPayments(workbook, 'TVA encaissements', 'Client', 'clientName', 'Encaissé TTC', exportData.vat.collectedDetail);
    if (exportData.hasPurchases) addVatPayments(workbook, 'TVA décaissements', 'Fournisseur', 'supplierName', 'Décaissé TTC', exportData.vat.paidDetail);
    if (exportData.controls.length > 0) addControls(workbook, exportData.controls);

    const journals = journalsSuffix(exportData);
    downloadWorkbook(workbook, `Export comptable du ${displayDate(exportData.period.start).replace(/\//g, '-')} au ${displayDate(exportData.period.end).replace(/\//g, '-')}${journals ? ` (${journals})` : ''}.xlsx`);
}
